import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/homeData';

export default function BasetaoQCProcess() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-20">
        <article className="max-w-3xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">Quality Control</span>
            <h1 className="text-2xl md:text-4xl font-heading text-foreground-950 mb-4">BaseTao QC Process: Quality Control Explained</h1>
            <p className="text-foreground-500 text-sm md:text-base max-w-xl mx-auto">Understand every step of the BaseTao quality control inspection process and how to use it for peace of mind.</p>
          </div>

          <div className="prose-custom space-y-8">
            <section>
              <h2 className="text-xl font-heading text-foreground-900 mb-4">What Is BaseTao QC?</h2>
              <p className="text-foreground-600 leading-relaxed mb-4"><strong className="text-foreground-900">BaseTao QC</strong> (Quality Control) is the inspection process that happens when your purchased items arrive at the BaseTao warehouse. Trained staff examine each product for defects, verify that the correct item was sent, check sizes and colors against your order, and photograph the products for your review.</p>
              <p className="text-foreground-600 leading-relaxed">This process is one of the biggest advantages of using BaseTao — you get eyes on your items before they ship internationally, giving you an opportunity to catch issues early.</p>
            </section>

            <section>
              <h2 className="text-xl font-heading text-foreground-900 mb-4">The QC Workflow Step by Step</h2>
              <div className="space-y-4">
                {[{step:'1',title:'Product Arrival',desc:'Your purchased item arrives at the BaseTao warehouse and is logged into the system. You receive a notification that your item has been received.'},{step:'2',title:'Visual Inspection',desc:'Staff examine the product for visible defects — tears, stains, missing parts, incorrect branding, or damaged packaging. Photos are taken from multiple angles.'},{step:'3',title:'Specification Check',desc:'The item is checked against your order: correct size, color, model number, and quantity. Any discrepancies are flagged immediately.'},{step:'4',title:'Photo Upload',desc:'Inspection photos are uploaded to your account. You can review them and decide whether to accept the item or request a return or exchange.'},{step:'5',title:'Storage or Action',desc:'If you approve, the item moves to storage awaiting shipment. If issues are found, BaseTao staff help coordinate with the seller for resolution.'}].map((s,i)=>(
                  <div key={i} className="flex gap-4 p-4 rounded-xl bg-background-100 border border-background-200/60">
                    <div className="w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center flex-shrink-0 text-sm font-bold">{s.step}</div>
                    <div>
                      <h3 className="font-heading text-sm text-foreground-900 mb-1">{s.title}</h3>
                      <p className="text-xs text-foreground-500 leading-relaxed">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-xl font-heading text-foreground-900 mb-4">How to Track QC in Your Spreadsheet</h2>
              <p className="text-foreground-600 leading-relaxed">Add a QC Status column to your <Link to="/basetao-spreadsheet/" className="text-primary-600 hover:text-primary-700 font-medium">BaseTao Spreadsheet</Link> with options like Awaiting QC, Under Inspection, QC Passed, and QC Issues Found. This gives you a dashboard view of every item&apos;s quality control progress. For warehouse process details, see our <Link to="/basetao-warehouse/" className="text-primary-600 hover:text-primary-700 font-medium">Warehouse guide</Link>.</p>
            </section>

            <div className="bg-background-100 border border-background-200/70 rounded-xl p-6 mt-10">
              <h3 className="font-heading text-lg text-foreground-900 mb-3">Shop with Confidence</h3>
              <p className="text-foreground-600 text-sm mb-4">BaseTao QC gives you peace of mind. Start shopping and track your items through the process.</p>
              <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-xl text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap">Shop Now <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-up-line text-sm"></i></div></a>
            </div>

            <div className="text-sm text-foreground-400 mt-8 pt-6 border-t border-background-200/60 flex flex-wrap gap-4">
              <Link to="/" className="hover:text-primary-600 transition-colors">&larr; Back to Home</Link>
              <Link to="/basetao-warehouse/" className="hover:text-primary-600 transition-colors">Warehouse Guide</Link>
              <Link to="/basetao-spreadsheet/" className="hover:text-primary-600 transition-colors">Spreadsheet Guide</Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />

      {/* Schema.org Article */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'BaseTao QC Process: Quality Control Explained',
            description: 'Understand every step of the BaseTao quality control inspection process and how to use it for peace of mind.',
            dateModified: '2026-08-10',
            datePublished: '2026-08-10',
            author: {
              '@type': 'Organization',
              name: 'BaseTao Sheet',
            },
            publisher: {
              '@type': 'Organization',
              name: 'BaseTao Sheet',
              logo: {
                '@type': 'ImageObject',
                url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/0dc5bc4081b05862da3d01553c9c9d93.png',
              },
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': 'https://basetaosheet.team/basetao-qc-process/',
            },
          }),
        }}
      />
    </div>
  );
}